import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { firmInfo } from '../data/mockData';
import { Phone, Mail, MapPin, Clock, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    caseType: '',
    message: '',
    contactMethod: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitStatus({ 
        type: 'success', 
        message: 'Thank you for your inquiry. We will contact you shortly!' 
      });
      setFormData({ name: '', email: '', phone: '', caseType: '', message: '', contactMethod: '' });
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Failed to send message. Please try again or call us directly.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    { icon: Phone, label: 'Call Us', value: firmInfo.phone, href: `tel:${firmInfo.phoneRaw}`, action: 'Call Now' },
    { icon: Mail, label: 'Email Us', value: firmInfo.email, href: `mailto:${firmInfo.email}`, action: 'Send Email' },
    { icon: MessageSquare, label: 'Text Us', value: firmInfo.phone, href: `sms:${firmInfo.phoneRaw}`, action: 'Send Text' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Contact <span className="text-blue-900">Us</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get your free case review today. We're available 24/7.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Get In Touch</h2>
              
              {/* Contact Methods */}
              <div className="space-y-6 mb-12">
                {contactMethods.map((method, index) => (
                  <a
                    key={index}
                    href={method.href}
                    className="flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-xl hover:border-blue-300 transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-900 transition-colors">
                      <method.icon className="w-5 h-5 text-blue-900 group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-500 text-sm">{method.label}</p>
                      <p className="text-gray-900 font-semibold">{method.value}</p>
                    </div>
                    <span className="text-blue-900 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      {method.action}
                    </span>
                  </a>
                ))}
              </div>

              {/* Office Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Our Office</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-blue-900 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-gray-900 font-medium">Address</p>
                      <p className="text-gray-600">{firmInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-blue-900 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-gray-900 font-medium">Hours</p>
                      <p className="text-gray-600">24/7 - We're always available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
              <div className="text-center mb-8">
                <p className="text-blue-900 text-sm font-semibold uppercase tracking-wider mb-2">
                  Free Case Review
                </p>
                <h2 className="text-2xl font-bold text-gray-900">
                  Tell Us About Your Case
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {submitStatus.message && (
                  <div className={`p-4 rounded-lg text-sm ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}
                <Input
                  type="text"
                  placeholder="Your Name *"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500 py-6"
                />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Input
                    type="email"
                    placeholder="Your Email *"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500 py-6"
                  />
                  <Input
                    type="tel"
                    placeholder="Your Phone *"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500 py-6"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Select
                    value={formData.caseType}
                    onValueChange={(value) => setFormData({ ...formData, caseType: value })}
                  >
                    <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500 focus:ring-blue-500 py-6 h-auto">
                      <SelectValue placeholder="Case Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="auto" className="text-gray-900 hover:bg-gray-100">Auto Accident</SelectItem>
                      <SelectItem value="truck" className="text-gray-900 hover:bg-gray-100">Truck Accident</SelectItem>
                      <SelectItem value="motorcycle" className="text-gray-900 hover:bg-gray-100">Motorcycle Accident</SelectItem>
                      <SelectItem value="medical" className="text-gray-900 hover:bg-gray-100">Medical Malpractice</SelectItem>
                      <SelectItem value="wrongful" className="text-gray-900 hover:bg-gray-100">Wrongful Death</SelectItem>
                      <SelectItem value="slip" className="text-gray-900 hover:bg-gray-100">Slip & Fall</SelectItem>
                      <SelectItem value="other" className="text-gray-900 hover:bg-gray-100">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={formData.contactMethod}
                    onValueChange={(value) => setFormData({ ...formData, contactMethod: value })}
                  >
                    <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500 focus:ring-blue-500 py-6 h-auto">
                      <SelectValue placeholder="Preferred Contact" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="email" className="text-gray-900 hover:bg-gray-100">Email</SelectItem>
                      <SelectItem value="phone" className="text-gray-900 hover:bg-gray-100">Phone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  placeholder="Tell us about your case..."
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500 resize-none"
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-900 hover:bg-blue-800 text-white font-bold py-6 text-lg rounded-md transition-all duration-300 hover:shadow-lg disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      SENDING...
                    </>
                  ) : (
                    'SEND MESSAGE'
                  )}
                </Button>
                <p className="text-gray-500 text-xs text-center">
                  By submitting this form, you agree to our privacy policy.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
